/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import clsx from 'clsx'
import { register } from '../core/_requests'
import { Link } from 'react-router-dom'
import { PasswordMeterComponent } from '../../../../_metronic/assets/ts/components'
import { useAuth } from '../core/Auth'
import FormikErrors from '../../../shared/FormikErrors/FormikErrors';
import axios from 'axios'
import { Image } from 'react-bootstrap'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'

const initialValues = {
  displayName: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  acceptTerms: false,
}

const registrationSchema = yup.object().shape({
  displayName: yup.string().trim()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Display name is required'),
  username: yup.string().trim()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Username is required'),
  email: yup.string().trim()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: yup.string()
    .min(6, 'Minimum 6 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
  confirmPassword: yup.string()
    .required('Password confirmation is required')
    .when('password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: yup.string().oneOf([yup.ref('password')], "Passwords do not match"),
    }),
  acceptTerms: yup.bool().required('You must accept the terms and conditions'),
})

export function Registration() {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [isEmailConfirmationEnabled, setIsEmailConfirmationEnabled] = useState(false)
  const [emailConfirmationLifespanHours, setEmailConfirmationLifespanHours] = useState(0);
  const { saveAuth } = useAuth()
  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        var { data: result } = await register(
          values.username,
          values.email,
          values.displayName,
          values.password,
          values.confirmPassword
        )
        setIsEmailConfirmationEnabled(result.isEmailConfirmationEnabled);
        setEmailConfirmationLifespanHours(result.tokenLifespanHours);
        setSuccess(true);
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        if (axios.isAxiosError(error) && error.response) {
          setStatus(error.response.data.Message);
        }
        else {
          setStatus(`The registration details is incorrect.`)
        }
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  return success ? (
    <>
      <div className="text-center">
        <Image className="h-95px mb-4" src={toAbsoluteUrl('/media/auth/ok_w.png')} />
        <h1 className='text-dark mb-3'>Registration Successful</h1>
        <p>Your account has been successfully created.</p>
        {isEmailConfirmationEnabled ? (
          <>
            <p>You need to verify your email address before getting started.</p>
            <p>We've sent an email verification link to your email at {formik.values.email}. The link is valid for {emailConfirmationLifespanHours} hours.</p>
            <Link to='/auth/login'>
              <button
                type='button'
                className='btn btn-lg btn-light-primary w-100 mb-5'
              >
                Go Back
          </button>
            </Link>
          </>
        ) : (
            <p>You can now <Link to='/auth/login' className='link-primary fw-bolder'>log in with your account</Link>.</p>
          )
        }
      </div>
    </>
  ) :
    (
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_signup_form'
        onSubmit={formik.handleSubmit}
      >
        {/* begin::Heading */}
        <div className='mb-10 text-center'>
          {/* begin::Title */}
          <h1 className='text-dark mb-3'>Create an Account</h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className='text-gray-400 fw-bold fs-4'>
            Already have an account?
          <Link to='/auth/login' className='link-primary fw-bolder' style={{ marginLeft: '5px' }}>
              Log in
          </Link>
          </div>
          {/* end::Link */}
        </div>
        {/* end::Heading */}

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {/* begin::Form group Display name */}
        <div className='fv-row mb-7'>
          <label className='form-label fw-bolder text-dark fs-6'>Display Name</label>
          <input
            placeholder='Display name'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('displayName')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': formik.touched.displayName && formik.errors.displayName },
              {
                'is-valid': formik.touched.displayName && !formik.errors.displayName,
              }
            )}
          />
          <FormikErrors formik={formik} fieldKey='displayName' />
        </div>
        {/* end::Form group */}

        {/* begin::Form group Username */}
        <div className='fv-row mb-7'>
          <label className='form-label fw-bolder text-dark fs-6'>Username</label>
          <input
            placeholder='Username'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('username')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': formik.touched.username && formik.errors.username },
              {
                'is-valid': formik.touched.username && !formik.errors.username,
              }
            )}
          />
          <FormikErrors formik={formik} fieldKey='username' />
        </div>
        {/* end::Form group */}


        {/* begin::Form group Email */}
        <div className='fv-row mb-7'>
          <label className='form-label fw-bolder text-dark fs-6'>Email</label>
          <input
            placeholder='Email'
            type='email'
            autoComplete='off'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': formik.touched.email && formik.errors.email },
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
          />
          <FormikErrors formik={formik} fieldKey='email' />
        </div>
        {/* end::Form group */}

        {/* begin::Form group Password */}
        <div className='mb-10 fv-row' data-kt-password-meter='true'>
          <div className='mb-1'>
            <label className='form-label fw-bolder text-dark fs-6'>Password</label>
            <div className='position-relative mb-3'>
              <input
                type='password'
                placeholder='Password'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              <FormikErrors formik={formik} fieldKey='password' />
            </div>
            {/* begin::Meter */}
            <div
              className='d-flex align-items-center mb-3'
              data-kt-password-meter-control='highlight'
            >
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
            </div>
            {/* end::Meter */}
          </div>
          <div className='text-muted'>
            Password must have at least 6 characters.
        </div>
        </div>
        {/* end::Form group */}

        {/* begin::Form group Confirm password */}
        <div className='fv-row mb-5'>
          <label className='form-label fw-bolder text-dark fs-6'>Confirm Password</label>
          <input
            type='password'
            placeholder='Password confirmation'
            autoComplete='off'
            {...formik.getFieldProps('confirmPassword')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword,
              },
              {
                'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
              }
            )}
          />
          <FormikErrors formik={formik} fieldKey='confirmPassword' />
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <div className='form-check form-check-custom form-check-solid'>
            <input
              className='form-check-input'
              type='checkbox'
              id='kt_login_toc_agree'
              {...formik.getFieldProps('acceptTerms')}
            />
            <label
              className='form-check-label fw-bold text-gray-700 fs-6'
              htmlFor='kt_login_toc_agree'
            >
              I Agree the{' '}
              <Link to='/auth/terms' className='ms-1 link-primary'>
                terms and conditions
            </Link>
            .
          </label>
            <FormikErrors formik={formik} fieldKey='acceptTerms' />
          </div>
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='text-center'>
          <button
            type='submit'
            id='kt_sign_up_submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid || !formik.values.acceptTerms}
          >
            {!loading && <span className='indicator-label'>Submit</span>}
            {loading && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <Link to='/auth/login'>
            <button
              type='button'
              id='kt_login_signup_form_cancel_button'
              className='btn btn-lg btn-light-primary w-100 mb-5'
            >
              Cancel
          </button>
          </Link>
        </div>
        {/* end::Form group */}
      </form >
    )
}
